/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import ClassPage from "./ClassPage";
import './CharacterModal.css';
import React, { useEffect, useState } from "react";
import BackgroundPage from "./BackgroundPage";
import AbilitiesPage from "./AbilitiesPage";
import Attributes from "../model/Attributes";
import Monster from "../model/Monster";
import Utils from "../model/Utils";
import Open5e from "../model/Open5e";
import ClassService from "../services/ClassService";
import RaceService from "../services/RaceService";
import Race from "../model/Race";
import BackgroundService from "../services/BackgroundService";
import SkillStats from "../model/SkillStats";
import CharacterService from "../services/CharacterService";
import ItemSection from "./ItemSection";
import ItemOption from '../model/ItemOption';
import SpellLevelsSelector from "./SpellLevelsSelector";
import SpellService from "../services/SpellService";
import Section from "../model/Section";
import RacePage from "./RacePage";
import Spell from '../model/Spell';
import NpcData from "../model/NpcData";
import SkillSet, { Skill } from "../model/SkillSet";
import AttributeValues from "../model/AttributeValues";
import Class from "../model/Class";
import Background from "../model/Background";
import ASIOption from "../model/ASIOption";
import { ClassDetails } from "../model/ClassDetails";
import DataModel from "../model/DataModel";
import CacheBlock from "../CacheBlock";
import FeatService from "../services/FeatService";
import Armor from "../model/Armor";
import Abilities from "../model/Abilities";
import ArmorSelector from "../ArmorSelector";

interface CharacterModalProps {
  isOpen: boolean;
  onClose: (mon: Monster | null) => void;
  mon: Monster;
  dataModel: DataModel
}

const CharacterModal: React.FC<CharacterModalProps> = ({ isOpen, onClose, mon, dataModel }) => {
    const initialNumbers = [15, 14, 13, 12, 10, 8]; // standard array

    const [npcName,                    setNpcName]                    = useState('NPC');
    const [activeTab,                  setActiveTab]                  = useState('race');
    const [selectedRace,               setSelectedRace]               = useState<Race | null>(null);
    const [primaryClassDetails,        setPrimaryClassDetails]        = useState<ClassDetails>(new ClassDetails);
    const [secondaryClassDetails,      setSecondaryClassDetails]      = useState<ClassDetails>(new ClassDetails);
    const [raceOptionsSelectedArrayByOptionNameMap, setRaceOptionsSelectedArrayByOptionNameMap] = useState(new Map());
    const [attributeBases,             setAttributeBases]             = useState<number[]>(initialNumbers);
    const [diceArray,                  setDiceArray]                  = useState('standard');
    const [attributeValues,            setAttributeValues]            = useState<AttributeValues>(new AttributeValues());
    const [asiRacialOptions,           setAsiRacialOptions]           = useState<ASIOption[]>([]);
    const [asiRacialSelected,          setAsiRacialSelected]          = useState<string[]>([]);
    const [asiClassOptions,            setAsiClassOptions]            = useState<ASIOption[]>([]);
    const [asiClassSelected,           setAsiClassSelected]           = useState<string[]>([]);
    const [langsRaceOptions,           setLangsRaceOptions]           = useState<ItemOption[]>([]);
    const [langsRaceSelected,          setLangsRaceSelected]          = useState<string[]>([]);
    const [langsClassOptions,          setLangsClassOptions]          = useState<ItemOption[]>([]);
    const [langsClassSelected,         setLangsClassSelected]         = useState<string[]>([]);
    const [langsBackgroundOptions,     setLangsBackgroundOptions]     = useState<ItemOption[]>([]);
    const [langsBackgroundSelected,    setLangsBackgroundSelected]    = useState<string[]>([]);
    const [skillsRaceOptions,          setSkillsRaceOptions]          = useState<ItemOption[]>([]);
    const [skillsRaceSelected,         setSkillsRaceSelected]         = useState<string[]>([]);
    const [skillsClassOptions,         setSkillsClassOptions]         = useState<ItemOption[]>([]);
    const [skillsClassSelected,        setSkillsClassSelected]        = useState<string[]>([]);
    const [skillsBackgroundOptions,    setSkillsBackgroundOptions]    = useState<ItemOption[]>([]);
    const [skillsBackgroundSelected,   setSkillsBackgroundSelected]   = useState<string[]>([]);
    const [armorRaceOptions,           setArmorRaceOptions]           = useState<ItemOption[]>([]);
    const [armorRaceSelected,          setArmorRaceSelected]          = useState<string[]>([]);
    const [armorClassOptions,          setArmorClassOptions]          = useState<ItemOption[]>([]);
    const [armorClassSelected,         setArmorClassSelected]         = useState<string[]>([]);
    const [armorBackgroundOptions,     setArmorBackgroundOptions]     = useState<ItemOption[]>([]);
    const [armorBackgroundSelected,    setArmorBackgroundSelected]    = useState<string[]>([]);
    const [weaponsRaceOptions,         setWeaponsRaceOptions]         = useState<ItemOption[]>([]);
    const [weaponsRaceSelected,        setWeaponsRaceSelected]        = useState<string[]>([]);
    const [weaponsClassOptions,        setWeaponsClassOptions]        = useState<ItemOption[]>([]);
    const [weaponsClassSelected,       setWeaponsClassSelected]       = useState<string[]>([]);
    const [weaponsBackgroundOptions,   setWeaponsBackgroundOptions]   = useState<ItemOption[]>([]);
    const [weaponsBackgroundSelected,  setWeaponsBackgroundSelected]  = useState<string[]>([]);
    const [equipmentRaceOptions,       setEquipmentRaceOptions]       = useState<ItemOption[]>([]);
    const [equipmentRaceSelected,      setEquipmentRaceSelected]      = useState<string[]>([]);
    const [equipmentClassOptions,      setEquipmentClassOptions]      = useState<ItemOption[]>([]);
    const [equipmentClassSelected,     setEquipmentClassSelected]     = useState<string[]>([]);
    const [equipmentBackgroundOptions, setEquipmentBackgroundOptions] = useState<ItemOption[]>([]);
    const [equipmentBackgroundSelected,setEquipmentBackgroundSelected]= useState<string[]>([]);
    const [selectedBackground,         setSelectedBackground]         = useState<Background | null>(null);
    const [multiClassError,            setMultiClassError]            = useState('Select a primary class first.');
    const [abilitiesDone,              setAbilitiesDone]              = useState(true);
    const [languagesDone,              setLanguagesDone]              = useState(true);
    const [skillsDone,                 setSkillsDone]                 = useState(true);
    const [cantripsDone,               setCantripsDone]               = useState(true);
    const [spellsDone,                 setSpellsDone]                 = useState(true);
    const [spellbookDone,              setSpellbookDone]              = useState(true);
    const [equipmentDone,              setEquipmentDone]              = useState(true);
    const [allowedSecondaryClassNames, setAllowedSecondaryClassNames] = useState<string[]>([]);
    const [races,                      setRaces]                      = useState<Race[]>([]);
    const [classes,                    setClasses]                    = useState<Class[]>([]);
    const [documentSources,            setDocumentSources]            = useState<Map<string, { name: string; licenses: string[]; }>>(new Map());
    const [backgrounds,                setBackgrounds]                = useState<Background[]>([]);
    const [spellsBySpellLevelByClass,  setSpellsBySpellLevelByClass]  = useState<Map<string, Map<number, Spell[]>>>(new Map());
    const [openLevel,                  setOpenLevel]                  = useState<number>(0);
    const [defaultClassName,           setDefaultClassName]           = useState<string>('Fighter');
    const [validateOnClassChange,      setValidateOnClassChange]      = useState<boolean>(false);
    const [featsByName,                setFeatsByName]                = useState<Map<string, Ability>>(new Map());
    const [armorBlockOpen,             setArmorBlockOpen]             = useState<boolean>(false);
    const [equipedArmor,               setEquipedArmor]               = useState<Armor | null>(null);
    const [equipedArmorClass,          setEquipedArmorClass]          = useState<number>(10);
    const [equipedArmorDesc,           setEquipedArmorDesc]           = useState<string>('');
    const [equipedWeapon1,             setEquipedWeapon1]             = useState<string>('');
    const [equipedWeapon2,             setEquipedWeapon2]             = useState<string>('');
    const [equipedWeapon3,             setEquipedWeapon3]             = useState<string>('');
    const [equipedWeapon1Conflict,     setEquipedWeapon1Conflict]     = useState<boolean>(false);
    const [equipedWeapon2Conflict,     setEquipedWeapon2Conflict]     = useState<boolean>(false);
    const [equipedWeapon3Conflict,     setEquipedWeapon3Conflict]     = useState<boolean>(false);
    const [onlyProfWeapons,            setOnlyProfWeapons]            = useState<boolean>(true);
    const [possibleWeaponNames,        setPossibleWeaponNames]        = useState<string[]>([]);
    const [profWeaponNames,            setProfWeaponNames]            = useState<string[]>([]);
    const [martialWeaponNames,         setMartialWeaponNames]         = useState<string[]>([]);
    const [simpleWeaponNames,          setSimpleWeaponNames]          = useState<string[]>([]);
    const [weaponProficiencies,        setWeaponProficiencies]        = useState<string[]>([]);
    const [armorProficiencies,         setArmorProficiencies]         = useState<string[]>([]);
    
    const [, setForceUpdate] = useState(0); // Dummy state to force re-renders

    const possibleSkills = [...SkillStats.getSkillsMap().keys(),
                            ...SkillStats.tools,
                            ...SkillStats.musicalInstruments,
                            ...SkillStats.gamingSets];

    const getFeats = async () => {
      const feats = await FeatService.fetchFeats(false, () => {});
      const map = new Map<string, Ability>();
      for (const feat of feats) {
        map.set(feat.name, feat);
      }
      setFeatsByName(map);
    }
    useEffect(() => {
      getFeats();
      // const lightArmors = Armor.armors.filter(a => 'light' === a.type).map(armor => armor.name);
      // const mediumArmors = Armor.armors.filter(a => 'medium' === a.type).map(armor => armor.name);
      // const heavyArmors = Armor.armors.filter(a => 'heavy' === a.type).map(armor => armor.name);

      const simpleNames = [...new Set(Abilities.weapons.filter(ab => ab.weapon_data?.simple === true).map(ab => ab.name))];
      const martialNames = [...new Set(Abilities.weapons.filter(ab => ab.weapon_data?.simple === false).map(ab => ab.name))];
      const possibleNames = [...new Set(Abilities.weapons.map(ab => ab.name))];
      setSimpleWeaponNames(simpleNames);
      setMartialWeaponNames(martialNames);
      setPossibleWeaponNames(possibleNames);
      
      //const possibleEquipment: string[] = [...SkillStats.packs, ...SkillStats.tools, ...SkillStats.gamingSets, ...SkillStats.musicalInstruments];
    }, []);

    useEffect(() => {
      const updateDataModel = () => {
        let human = races?.filter(r => r.name.toLowerCase() === 'human');
        let defaultClass = classes?.filter(c => c.name.toLowerCase() === defaultClassName.toLowerCase());
        let defaultBackground = backgrounds?.filter(c => c.name.toLowerCase() === 'none');
        setDocumentSources(dataModel.documents);
        setClasses(dataModel.classes.data);
        setRaces(dataModel.races.data);
        setBackgrounds(dataModel.backgrounds.data);
        setSpellsBySpellLevelByClass(SpellService.parseSpells(dataModel.spells.data));

        // Set the default selections
        if (!human) {
          human = dataModel.races.data.filter(r => r.name.toLowerCase() === 'human');
          human && human.length && handleRaceChange(human[0]);
        }

        if (!defaultClass) {
          defaultClass = dataModel.classes.data.filter(c => c.name.toLowerCase() === defaultClassName.toLowerCase());
          if (defaultClass && defaultClass.length) {
            const detail = new ClassDetails();
            detail.classInfo = defaultClass[0]
            detail.classLevel = primaryClassDetails?.classLevel || 1;
            setPrimaryClassDetails(detail);
          }
        }

        if (!defaultBackground) {
          defaultBackground = dataModel.backgrounds.data.filter(c => c.name.toLowerCase() === 'none');
          defaultBackground && defaultBackground.length && handleBackgroundChanges(defaultBackground[0]);
        }

        // This update just sets a member variable to force a redraw:
        setForceUpdate((prev) => prev + 1);
      }

      // Cleanup on unmount
      return dataModel.subscribe(() => updateDataModel());
    }, [dataModel]);

    const handleRaceChange = (race: Race | null): void => {
        setSelectedRace(race);
        setAbilitiesDone(false);
        setLanguagesDone(false);
        setSkillsDone(false);

        const asis:ASIOption[] = [];
        const selectedRaceAsis = [];
        for (const raceAsi of race?.asi || []) {
            const asi:ASIOption = {
                label: race?.name.split(/\s/)[0] ?? '',
                attribute: raceAsi.attributes[0],
                level: raceAsi.value,
                feat: null,
                pairedASI: null
            };
            asis.push(asi);
            if (asi.attribute !== 'Other' && asi.attribute !== 'Any') {
                selectedRaceAsis.push(asi.attribute + ":" + asi.level);
            }
        }
        setAsiRacialOptions(asis);
        setAsiRacialSelected(selectedRaceAsis);

        const racialSkills: ItemOption[] = [];
        const selectRacialSkills: string[] = [];
        RaceService.parseSkills(race, racialSkills, selectRacialSkills);
        setSkillsRaceOptions(racialSkills);
        setSkillsRaceSelected(selectRacialSkills);

        const racialLangs: ItemOption[] = [];
        const selectRacialLangs: string[] = [];
        RaceService.parseLanguages(race, racialLangs, selectRacialLangs);
        setLangsRaceOptions(racialLangs);
        setLangsRaceSelected(selectRacialLangs);

        const racialArmor: ItemOption[] = [];
        const selectRacialArmor: string[] = [];
        RaceService.parseArmorProficiency(race, racialArmor, selectRacialArmor);
        setArmorRaceOptions(racialArmor);
        setArmorRaceSelected(selectRacialArmor);

        const racialWeapons: ItemOption[] = [];
        const selectRacialWeapons: string[] = [];
        RaceService.parseWeaponProficiency(race, racialWeapons, selectRacialWeapons);
        setWeaponsRaceOptions(racialWeapons);
        setWeaponsRaceSelected(selectRacialWeapons);
    }

    const handleUpdateArmor = (ac: number, desc: string, armor: Armor | null): void => {
      setEquipedArmorClass(ac);
      setEquipedArmorDesc(desc);
      setEquipedArmor(armor);
    }

    const mapToString = (map: Map<string, string[]>): string => {
      const results = [];
      for (const key of map.keys()) {
        const valString = map.get(key)?.join(':') || '';
        results.push(`${key.trim()}:${valString}`);
      }
      return results.join("|");
    }
    const stringToMap = (str: string): Map<string, string[]> => {
      const results = new Map();
      for (const entry of (str?.split('|') || [])) {
        const values = entry.split(':');
        if (values.length > 1) {
          const [name, ...valueArray] = values;
          results.set(name.trim(), valueArray);
        }
      }
      return results;
    }

    useEffect(() => {
      if (mon?.npcData && classes?.length) {
        const npcData: NpcData = mon.npcData as NpcData;
        setNpcName(npcData.name);
        let items;
        if (races) {
          items = races.filter(race => race.name == npcData.raceName);
          if (items?.length > 0) {
            handleRaceChange(items[0]);
          }
        }

        const primaryClasses = classes.filter(cls => cls.name.toLowerCase() === npcData.primaryClassName.toLowerCase());
        const primaryDetail = new ClassDetails();
        primaryDetail.classInfo = primaryClasses && primaryClasses[0] || null;
        primaryDetail.classLevel = Math.min(20, npcData.primaryLevel || 1);
        primaryDetail.optionsSelectedArrayByOptionNameMap = stringToMap(npcData.primaryClassOptions);
        const primaryArchetypes = classes.filter(cls => cls.name == npcData.primaryArchetypeName);
        primaryDetail.archetype = primaryArchetypes && primaryArchetypes[0] || null;
        primaryDetail.getSections();
        primaryDetail.spellbookSpellsArrayBySpellLevel = [...npcData.spellbookSpellsArrayBySpellLevelPrimary || []];
        primaryDetail.spellsPreparedArrayBySpellLevel = [...npcData.spellsPreparedArrayBySpellLevelPrimary || []];
        setPrimaryClassDetails(primaryDetail);

        const secondayClasses = classes.filter(cls => cls.name.toLowerCase() === npcData.secondaryClassName.toLowerCase());
        const secondaryDetail = new ClassDetails();
        secondaryDetail.classInfo = secondayClasses && secondayClasses[0] || null;
        secondaryDetail.classLevel = Math.min(20, npcData.secondaryLevel || 1);
        secondaryDetail.optionsSelectedArrayByOptionNameMap = stringToMap(npcData.secondaryClassOptions);
        const secondaryArchetypes = classes.filter(cls => cls.name == npcData.secondaryArchetypeName);
        secondaryDetail.archetype = secondaryArchetypes && secondaryArchetypes[0] || null;
        secondaryDetail.getSections();
        secondaryDetail.spellbookSpellsArrayBySpellLevel = [...npcData.spellbookSpellsArrayBySpellLevelSecondary || []];
        secondaryDetail.spellsPreparedArrayBySpellLevel = [...npcData.spellsPreparedArrayBySpellLevelSecondary || []];
        setSecondaryClassDetails(secondaryDetail);

        if (backgrounds) {
          items = backgrounds.filter(background => background.name == npcData.backgroundName);
          if (items?.length > 0) {
            handleBackgroundChanges(items[0]);
          }
        }
        let attribs = npcData.attributeBases;
        if (!attribs || !attribs.length) {
          attribs = initialNumbers;
        }
        setAttributeBases(attribs);
        setDiceArray(npcData.diceArray || 'standard');
        setAttributeValues(npcData.attributeValues || initialNumbers);
        setAsiRacialSelected(npcData.asiRacialSelected || []);
        setAsiClassSelected(npcData.asiClassSelected || []);
        // lookup the background by the npcData.backgroundName:
        const background = backgrounds.filter(b => b.name == npcData.backgroundName);
        setSelectedBackground((background.length > 0) ? background[0] : null);
        setRaceOptionsSelectedArrayByOptionNameMap(stringToMap(npcData.raceOptions));
        setLangsRaceSelected([...npcData.langsRaceSelected]);
        setLangsClassSelected([...npcData.langsClassSelected]);
        setLangsBackgroundSelected([...npcData.langsBackgroundSelected]);
        setSkillsRaceSelected([...npcData.skillsRaceSelected]);
        setSkillsClassSelected([...npcData.skillsClassSelected]);
        setSkillsBackgroundSelected([...npcData.skillsBackgroundSelected]);

        setValidateOnClassChange(true);

        setEquipedArmor(mon.armor);
        setEquipedArmorClass(mon.armor_class);
        setEquipedArmorDesc(mon.armor_desc);

      } else if (mon.npcData) {
        setDefaultClassName(mon.npcData.primaryClassName);
      }
    }, [mon.npcData, races, classes, backgrounds]);

    const validateSkills = (classSkills: ItemOption[], skillsSelected: string[], selectedClassSkills: string[]): string[] => {
      for (let i=0 ; i<classSkills.length ; i++) {
        if (classSkills[i].options.includes(skillsSelected[i])) {
          selectedClassSkills[i] = skillsSelected[i];
        }
      }
      return selectedClassSkills;
    }

  useEffect(() => {
      const asis: ASIOption [] = [];
      for (const detail of [primaryClassDetails, secondaryClassDetails]) {
        if (detail?.classInfo?.getAsiLevels) {
          const asiLevels = detail.classInfo.getAsiLevels(detail.classLevel);
          for (const asiLevel of asiLevels || []) {
            const asi1: ASIOption = {
                label: detail.classInfo.name + '-' + asiLevel,
                attribute: 'AnyFeat',
                level: 1,
                feat: null,
                pairedASI: null
            };
            const asi2: ASIOption = {
                label: detail.classInfo.name + '-' + asiLevel,
                attribute: 'Any',
                level: 1,
                feat: null,
                pairedASI: null
            };
            asi1.pairedASI = asi2;
            asi2.pairedASI = asi1;
            asis.push(asi1);
            asis.push(asi2);
          }
        }
      }

      primaryClassDetails.getSections();
      secondaryClassDetails.getSections();
      setAsiClassOptions(asis);
      if (asiClassSelected.length > asis.length) {
          setAsiClassSelected(asiClassSelected.slice(0, asis.length));
      }

      const classLangs: ItemOption[] = [];
      const selectedClassLangs: string[] = [];
      ClassService.parseLanguages(primaryClassDetails.classInfo || null, classLangs, selectedClassLangs);
      ClassService.parseLanguages(secondaryClassDetails.classInfo || null, classLangs, selectedClassLangs);
      setLangsClassOptions(classLangs);
      setLangsClassSelected(selectedClassLangs);

      if (primaryClassDetails?.classInfo) {
        const classSkills: ItemOption[] = [];
        const selectedClassSkills:string[] = [];
        ClassService.parseSkills(primaryClassDetails, classSkills, selectedClassSkills, true);
        if (secondaryClassDetails?.classInfo) {
          ClassService.parseSkills(secondaryClassDetails, classSkills, selectedClassSkills, false);
        }
        setSkillsClassOptions(classSkills);

        const classArmors: ItemOption[] = [];
        const selectedClassArmors:string[] = [];
        ClassService.parseArmor(primaryClassDetails.classInfo, classArmors, selectedClassArmors, true);
        if (secondaryClassDetails?.classInfo) {
          ClassService.parseArmor(secondaryClassDetails.classInfo, classArmors, selectedClassArmors, false);
        }
        setArmorClassOptions(classArmors);

        const classWeapons: ItemOption[] = [];
        const selectedClassWeapons:string[] = [];
        ClassService.parseWeapons(primaryClassDetails.classInfo, classWeapons, selectedClassWeapons, true);
        if (secondaryClassDetails?.classInfo) {
          ClassService.parseWeapons(secondaryClassDetails.classInfo, classWeapons, selectedClassWeapons, false);
        }
        setWeaponsClassOptions(classWeapons);

        const classEquipment: ItemOption[] = [];
        const selectedClassEquipment:string[] = [];
        ClassService.parseEquipment(primaryClassDetails.classInfo, classEquipment, selectedClassEquipment, true);
        if (secondaryClassDetails?.classInfo) {
          ClassService.parseEquipment(secondaryClassDetails.classInfo, classEquipment, selectedClassEquipment, false);
        }
        setEquipmentClassOptions(classEquipment);

        if (validateOnClassChange) {
          const newSkillsClassSelected = validateSkills(classSkills, skillsClassSelected, selectedClassSkills);
          setSkillsClassSelected(newSkillsClassSelected);

          const newArmorClassSelected = validateSkills(classArmors, armorClassSelected, selectedClassArmors);
          setArmorClassSelected(newArmorClassSelected);

          const newClassWeaponsSelected = validateSkills(classWeapons, weaponsClassSelected, selectedClassWeapons);
          setWeaponsClassSelected(newClassWeaponsSelected);

          const newClassEquipmentSelected = validateSkills(classEquipment, equipmentClassSelected, selectedClassEquipment);
          setEquipmentClassSelected(newClassEquipmentSelected);
        }
        primaryClassDetails.validateSpells(spellsBySpellLevelByClass);
        primaryClassDetails.validateSelectedOptions();
      }
      if (secondaryClassDetails) {
        secondaryClassDetails.validateSpells(spellsBySpellLevelByClass);
        secondaryClassDetails.validateSelectedOptions();
      }
    }, [primaryClassDetails.classInfo,   primaryClassDetails.classLevel,   primaryClassDetails.archetype,
        secondaryClassDetails.classInfo, secondaryClassDetails.classLevel, secondaryClassDetails.archetype]);

    useEffect(() => {
        checkMultiClassRequirements(attributeValues);
    }, [attributeValues, primaryClassDetails.classInfo])
    
    useEffect(() => {
        const raceAsisComplete = (asiRacialOptions.length == asiRacialSelected.filter(o =>o).length);
        const classAsisComplete = (asiClassOptions.length == asiClassSelected.filter(o =>o).length);
        setAbilitiesDone(raceAsisComplete && classAsisComplete);
        for (let i=0; (i+1)<asiClassSelected.length ; i+=2) {
          if (asiClassSelected[i] != 'Feat' && Attributes.getAttrName(asiClassSelected[i+1]) == '') {
            asiClassSelected[i+1] = '';
          }
        }
    }, [asiRacialOptions, asiClassOptions, asiRacialSelected, asiClassSelected]);

    useEffect(() => {
        const raceLangComplete = (langsRaceOptions.length == langsRaceSelected.filter(o =>o).length);
        const classLangComplete = (langsClassOptions.length == langsClassSelected.filter(o =>o).length);
        const backgroundLangComplete = (langsBackgroundOptions.length == langsBackgroundSelected.filter(o =>o).length);
        setLanguagesDone(raceLangComplete && classLangComplete && backgroundLangComplete);
    }, [langsRaceOptions, langsRaceSelected, langsClassOptions, langsClassSelected, langsBackgroundOptions, langsBackgroundSelected]);

    useEffect(() => {
        const raceSkillsComplete = (skillsRaceOptions.length == skillsRaceSelected.filter(o =>o).length);
        const classSkillsComplete = (skillsClassOptions.length == skillsClassSelected.filter(o =>o).length);
        const backgroundSkillsComplete = (skillsBackgroundOptions.length == skillsBackgroundSelected.filter(o =>o).length);
        setSkillsDone(raceSkillsComplete && classSkillsComplete && backgroundSkillsComplete);
    }, [skillsRaceOptions, skillsRaceSelected,
        skillsClassOptions, skillsClassSelected,
        skillsBackgroundOptions, skillsBackgroundSelected]);

    useEffect(() => {
      const hasShield = equipedArmorDesc?.toLowerCase().includes('shield') || false;
      const twoHandedWeaponNames = Abilities.weapons.filter(ab => ab.weapon_data?.handed === 'two').map(ab => ab.name);
      setEquipedWeapon1Conflict(hasShield && !!equipedWeapon1 && twoHandedWeaponNames.includes(equipedWeapon1));
      setEquipedWeapon2Conflict(hasShield && !!equipedWeapon2 && twoHandedWeaponNames.includes(equipedWeapon2));
      setEquipedWeapon3Conflict(hasShield && !!equipedWeapon3 && twoHandedWeaponNames.includes(equipedWeapon3));
    }, [equipedWeapon1, equipedWeapon2, equipedWeapon3, equipedArmorDesc]);

    useEffect(() => {
        const raceArmorComplete = (armorRaceOptions.length == armorRaceSelected.filter(o =>o).length);
        const classArmorComplete = (armorClassOptions.length == armorClassSelected.filter(o =>o).length);
        const backgroundArmorComplete = (armorBackgroundOptions.length == armorBackgroundSelected.filter(o =>o).length);
        const raceWeaponsComplete = (weaponsRaceOptions.length == weaponsRaceSelected.filter(o =>o).length);
        const classWeaponsComplete = (weaponsClassOptions.length == weaponsClassSelected.filter(o =>o).length);
        const backgroundWeaponsComplete = (weaponsBackgroundOptions.length == weaponsBackgroundSelected.filter(o =>o).length);
        setEquipmentDone(raceArmorComplete && classArmorComplete && backgroundArmorComplete && 
                         raceWeaponsComplete && classWeaponsComplete && backgroundWeaponsComplete);
    }, [weaponsRaceOptions, weaponsRaceSelected,
        weaponsClassOptions, weaponsClassSelected,
        weaponsBackgroundOptions, weaponsBackgroundSelected,
        armorRaceOptions, armorRaceSelected,
        armorClassOptions, armorClassSelected,
        armorBackgroundOptions, armorBackgroundSelected]);

    useEffect(() => {
        const raceEquipmentComplete = (equipmentRaceOptions.length == equipmentRaceSelected.filter(o =>o).length);
        const classEquipmentComplete = (equipmentClassOptions.length == equipmentClassSelected.filter(o =>o).length);
        const backgroundEquipmentComplete = (equipmentBackgroundOptions.length == equipmentBackgroundSelected.filter(o =>o).length);
        setEquipmentDone(raceEquipmentComplete && classEquipmentComplete && backgroundEquipmentComplete);
    }, [equipmentRaceOptions, equipmentRaceSelected,
        equipmentClassOptions, equipmentClassSelected,
        equipmentBackgroundOptions, equipmentBackgroundSelected]);

    useEffect(() => {
      let weaponProfs = [...new Set([...weaponsRaceSelected, ...weaponsClassSelected, ...weaponsBackgroundSelected])].sort();
      // remove empty rows (caused when the user doesn't select an option in the skills tab)
      weaponProfs = weaponProfs.filter(o => o);
      let fullProfWeaponNames: string[] = possibleWeaponNames.filter(wn => weaponProfs.includes(wn));

      if (weaponProfs.includes('Martial Weapons')) {
        weaponProfs = weaponProfs.filter(o => !martialWeaponNames.includes(o));
        fullProfWeaponNames = [...fullProfWeaponNames, ...martialWeaponNames];
      }
      if (weaponProfs.includes('Simple Weapons')) {
        weaponProfs = weaponProfs.filter(o => !simpleWeaponNames.includes(o));
        fullProfWeaponNames = [...fullProfWeaponNames, ...simpleWeaponNames];
      }
      setWeaponProficiencies(weaponProfs);
      setProfWeaponNames([...new Set(fullProfWeaponNames)].sort());
    }, [weaponsRaceSelected, weaponsClassSelected, weaponsBackgroundSelected]);

    useEffect(() => {
      let armorProfs = [...new Set([...armorRaceSelected, ...armorClassSelected, ...armorBackgroundSelected])].sort();
      // remove empty rows (caused when the user doesn't select an option in the skills tab)
      armorProfs = armorProfs.filter(o => o);

      if (armorProfs.includes('All Armor')) {
        armorProfs = armorProfs.filter(o => !['Light Armor', 'Medium Armor', 'Heavy Armor'].includes(o));
      }
      setArmorProficiencies(armorProfs);
    }, [armorRaceSelected, armorClassSelected, armorBackgroundSelected]);

    useEffect(() => {

      let cantripsReady = true;
      let spellsReady = true;
      let spellbookReady = true;
      for (const info of [primaryClassDetails, secondaryClassDetails]) {
        if (!info?.classInfo) {
          continue;
        }
        const cls = info.classInfo;
        const arch = info.archetype;
        const level = info.classLevel;
        const spellBook = info.spellbookSpellsArrayBySpellLevel;
        const preped = info.spellsPreparedArrayBySpellLevel;

        if (cls.name === 'Wizard') {
          // make sure we have prepared spells from the spellbook
          for (let spellLevel = 0 ; spellLevel < 10 ; spellLevel++) {
            const totalKnown = spellBook[spellLevel]?.length || 0;
            const maxKnown = SpellService.spellbookSpellsKnowableByWizardLevel[level][spellLevel];
            if (totalKnown < maxKnown) {
              spellbookReady = false;
              console.log(`spells not ready: spellbook at level ${spellLevel}, prepped: ${totalKnown}, max: ${maxKnown}`);
            }
          }
        }

        const cantripsKnownMax: number = ClassService.getCantripsKnonwn(cls, level) ||
                                        (arch ? ClassService.getCantripsKnonwn(arch, level) : 0);
        if ((preped[0]?.length || 0) < cantripsKnownMax) {
          cantripsReady = false;
          console.log(`cantrips not ready: ${cls.name}/${arch?.name}, cantrips prepped: ${preped[0]?.length || 0}, max: ${cantripsKnownMax}`);
        }

        const spellsPreppedMax: number = ClassService.getSpellsKnownForClassLevel(cls, level, attributeValues);
        let totalPrepped = 0;
        for (let spellLevel = 1 ; spellLevel < 10 ; spellLevel++) {
          totalPrepped += preped[spellLevel]?.length || 0;
        }
        if (totalPrepped < spellsPreppedMax) {
          spellsReady = false;
          console.log(`spells not ready: ${cls.name}/${arch?.name}, total prepped: ${totalPrepped}, max: ${spellsPreppedMax}`);
        }
      }
      setSpellbookDone(spellbookReady);
      setCantripsDone(cantripsReady);
      setSpellsDone(spellsReady);
    }, [primaryClassDetails.spellbookSpellsArrayBySpellLevel, secondaryClassDetails.spellbookSpellsArrayBySpellLevel,
        primaryClassDetails.spellsPreparedArrayBySpellLevel, secondaryClassDetails.spellsPreparedArrayBySpellLevel,
        spellsBySpellLevelByClass]);

    const handleTabClick = (tab: string) => {
      setActiveTab(tab);
    };

    const handleBackgroundChanges = (bg: Background | null) => {
      setSelectedBackground(bg);
      const backgroundLangs: ItemOption[] = [];
      const selectedBackgroundLangs: string[] = [];
      if (bg) {
        BackgroundService.parseLanguages(bg, backgroundLangs, selectedBackgroundLangs);
      }
      setLangsBackgroundOptions(backgroundLangs);
      setLangsBackgroundSelected(selectedBackgroundLangs);

      const backgroundSkills: ItemOption[] = [];
      const selectBackgroundSkills: string[] = [];
      if (bg) {
        BackgroundService.parseSkills(bg, backgroundSkills, selectBackgroundSkills);
      }
      setSkillsBackgroundOptions(backgroundSkills);
      setSkillsBackgroundSelected(selectBackgroundSkills);

      const backgroundEquipment: ItemOption[] = [];
      const selectBackgroundEquipment: string[] = [];
      if (bg) {
        BackgroundService.parseEquipment(bg, backgroundEquipment, selectBackgroundEquipment);
      }
      setEquipmentBackgroundOptions(backgroundEquipment);
      setEquipmentBackgroundSelected(selectBackgroundEquipment);
    }

    const checkMultiClassRequirements = (attributeVals: AttributeValues) => {
        let error = ''
        if (primaryClassDetails) {
            const uniqueErrors = ClassService.checkMultiClassRequirement(primaryClassDetails.classInfo?.base?.name || primaryClassDetails.classInfo?.name || '', attributeVals);
            if (uniqueErrors.size > 0) {
                error = `You need an attribute level of 13 in the following attributes: ${[...uniqueErrors].join(', ')}`;
            }
        } else {
            error = 'Select a primary class first.';
        }
        if (multiClassError != error) {
            setMultiClassError(error);
        }

        const classNames = classes.map(c => c.name);
        const nonPrimaryClasses = primaryClassDetails ? classNames.filter((className) => className != (primaryClassDetails.classInfo?.base?.name || primaryClassDetails.classInfo?.name)) : [];
        const allowedSecondaryClasses = nonPrimaryClasses.filter((className) => ClassService.checkMultiClassRequirement(className, attributeVals).size == 0);
        if (allowedSecondaryClasses.join(',') != allowedSecondaryClassNames.join(',')) {
            setAllowedSecondaryClassNames(allowedSecondaryClasses);
            if (secondaryClassDetails.classInfo && !allowedSecondaryClasses.includes(secondaryClassDetails.classInfo.base?.name || secondaryClassDetails.classInfo.name)) {
                secondaryClassDetails.clear();
            }
        }
    }

    if (!isOpen) {
        return null;
    }
    const handleAttributeValuesChanged = (attributeBaseValues: number[], attributeValues: AttributeValues) => {
        setAttributeBases(attributeBaseValues);
        setAttributeValues(attributeValues);
        checkMultiClassRequirements(attributeValues);
    };

    const getIncompleteMessage = () => {
        const errors = [];
        if (!primaryClassDetails?.classInfo) errors.push("Class (Required)");
        if (!npcName) errors.push("Name");
        if (!selectedRace) errors.push("Race");
        if (!abilitiesDone) errors.push("ASI increases");
        if (!selectedBackground) errors.push("Background");
        if (!languagesDone) errors.push("Languages");
        if (!skillsDone) errors.push("Skills");
        if (!spellbookDone) errors.push("Spellbook");
        if (!cantripsDone) errors.push("Cantrips");
        if (!spellsDone) errors.push("Spells");
        if (!equipmentDone) errors.push("Equipment");
        if (errors.length === 0) {
            return null;
        }
        return `Fill in the following items: ${errors.join(', ')}`;
    }

    const onAccept = () => {
        if (!primaryClassDetails.classInfo) {
          return;
        }
        const incomplete = getIncompleteMessage();
        if (incomplete) {
            if (!window.confirm("Some elements of this character are incomplete: \n" + incomplete +
                                "\nAre you sure you want to accept this incomplete character?"
                )) {
            return;
          }
        }
        const combinedLevel: number = primaryClassDetails.classLevel + (secondaryClassDetails.classInfo ? (secondaryClassDetails.classLevel || 0) : 0);
        let hitDiceType: number = parseInt(primaryClassDetails.classInfo.hit_dice.split('d')[1]);
        const hitDiceTypePrimary: number = hitDiceType;
        const hitDiceBonus: number = Attributes.getBonus(attributeValues.constitution) * combinedLevel;
        const hitDiceBonusStr = (hitDiceBonus == 0) ? '' : ((hitDiceBonus > 0 ? ' +' : ' ') + hitDiceBonus);
        // first level (of only primary class) always gets full hit die value.
        let hitPoints = hitDiceTypePrimary + hitDiceBonus + (combinedLevel-1) * (hitDiceType/2 + 1);
        let hitDice = `${combinedLevel}d${hitDiceType}${hitDiceBonusStr}`;
        if (secondaryClassDetails.classInfo && secondaryClassDetails.classInfo.hit_dice != secondaryClassDetails.classInfo.hit_dice) {
            const hitDiceTypeSecondary  = parseInt(secondaryClassDetails.classInfo.hit_dice.split('d')[1]);
            hitPoints = hitDiceTypePrimary + hitDiceBonus + 
                        ((primaryClassDetails.classLevel-1) * (hitDiceTypePrimary/2 + 1)) +
                        (secondaryClassDetails.classLevel * (hitDiceTypeSecondary/2 + 1));
            hitDice = `${primaryClassDetails.classLevel}d${hitDiceTypePrimary} + ${secondaryClassDetails.classLevel}d${hitDiceTypeSecondary}${hitDiceBonusStr}`;
            hitDiceType = (hitDiceTypePrimary + hitDiceTypeSecondary)/2;
        }
        const senses = RaceService.parseSenses(selectedRace);
        let npcDesc = `${selectedBackground?.name ?? ''} ${selectedRace?.name ?? ''} ${selectedRace || selectedRace ? ':' : ''} **${primaryClassDetails.classInfo.name}**-${primaryClassDetails.classLevel}`;
        if (primaryClassDetails.archetype) {
          npcDesc += ` (${primaryClassDetails.archetype.name})`;
        }
        if (secondaryClassDetails.classInfo) {
          npcDesc += ` / **${secondaryClassDetails.classInfo.name}**-${secondaryClassDetails.classLevel}`;
        }
        if (secondaryClassDetails.archetype) {
          npcDesc += ` (${secondaryClassDetails.archetype.name})`;
        }
        let sections: Section[] = [new Section('', npcDesc)];
        const langs = [...langsRaceSelected, ...langsClassSelected, ...langsBackgroundSelected];
        const speed = RaceService.parseSpeed(selectedRace);
        const size  = RaceService.parseSize(selectedRace);
        RaceService.processRace(selectedRace, raceOptionsSelectedArrayByOptionNameMap, sections);
        ClassService.processClass(primaryClassDetails, sections);
        if (secondaryClassDetails) {
          ClassService.processClass(secondaryClassDetails, sections);
        }
        for (let index=0 ; index<asiClassSelected.length ; index++) {
          const feat = featsByName.get(asiClassSelected[index]);
          if (feat) {
            sections.push(new Section( feat.name, feat.desc));
          }
        }

        Section.simplifySections(sections);

        const hitPointsCustom = `${hitPoints} (${hitDice})`;
        const proficiencyBonus = Utils.getProfBonusByCR(combinedLevel);
        //const proficientToolsArray = CharacterService.splitItems(selectedBackground?.tool_proficiencies ?? null, []).sort();
        let equipmentArray = CharacterService.splitItems(selectedBackground?.equipment ?? null, []);

        // remove duplicates:
        const allSkills = [... new Set([...skillsRaceSelected, ...skillsClassSelected, ...skillsBackgroundSelected])];
        // Not all the skills selected are in the skills set for monsters. As we identify these, we'll put them in an abilities block:
        const overflowProfSkills: string[] = [];
        const overflowExpertSkills: string[] = [];
        let skillSet = new SkillSet();
        for (const skName of allSkills) {
          const isExpert = skName.endsWith(SkillStats.EXPERT);
          const skillName = isExpert ? skName.replace(SkillStats.EXPERT, '') : skName;
          const attribute = SkillStats.getSkillsMap().get(skillName);
          if (attribute) {
            const skill = skillSet.skills[skillName];
            if (!skill) {
              const skill = new Skill();
              skill.attribute = attribute;
              skill.level = isExpert ? 'expert' : 'proficient';
              skillSet.skills[skillName] = skill;
            } else {
              // avoid downgrading a skill from expert to proficient:
              skill.level = isExpert ? 'expert' : skill.level;
            }
          } else {
            if (isExpert) {
              overflowExpertSkills.push(skillName);
              const index = overflowProfSkills.indexOf(skillName);
              if (index >= 0) {
                overflowProfSkills.splice(index, 1);
              }
            } else {
              if (!overflowExpertSkills.includes(skillName)) {
                overflowProfSkills.push(skillName);
              }
            }
          }
        }
        skillSet = SkillSet.updateLevels(skillSet, attributeValues, proficiencyBonus);

        const desc = [];
        if (overflowProfSkills.length) {
          desc.push(`* **Proficient:** ${overflowProfSkills.join(', ')}`);
        }
        if (overflowExpertSkills.length) {
          desc.push(`* **Expert:** ${overflowExpertSkills.join(', ')}`);
        }
        // if (proficientToolsArray.length) {
        //   desc.push(`* **Tools:** ${proficientToolsArray.join(', ')}`);
        // }
        if (desc.length) {
          sections.push(new Section('Additional Skills', desc.join('\n')));
        }

        equipmentArray = CharacterService.splitItems(primaryClassDetails.classInfo.equipment, equipmentArray);
        // secondary classes don't grant equipment

        const bonus_actions: Section[] = sections.filter(section => Section.isBonusAction(section));
        sections = sections.filter(section => !Section.isBonusAction(section));
        const reactions: Section[] = sections.filter(section => Section.isReaction(section));
        sections = sections.filter(section => !Section.isReaction(section));
        const spellSections: Section[] = sections.filter(section => Section.isSpell(section));
        sections = sections.filter(section => !Section.isSpell(section));

        const damageResistancesArray = [];
        // convert any damage resistances into the damage resistance array:
        for (let index=0 ; index<sections.length ; index++) {
          const desc = sections[index].desc.toLowerCase();
          if (desc.includes('you have resistance')) {
            for (const res of Utils.damageTypes) {
              if ((desc.includes(`you have resistance to ${res.toLowerCase()}`)) ||
                  (desc.includes(`you have resistance against ${res.toLowerCase()}`))) {
                  damageResistancesArray.push(res);
                if (desc == `you have resistance to ${res.toLowerCase()}.`) {
                  sections.splice(index, 1);
                  index--;
                }
              }
            }
          }
        }
        if (selectedBackground?.feature && selectedBackground?.feature_desc) {
          sections.push(new Section(selectedBackground.feature, selectedBackground.feature_desc));
        }

        sections = Section.removeAbilitiesThatHaveBeenHandled(sections);

        // validate spells & options for each class:
        if (primaryClassDetails) {
          primaryClassDetails.validateSpells(spellsBySpellLevelByClass);
          primaryClassDetails.validateSelectedOptions();
        }
        if (secondaryClassDetails) {
          secondaryClassDetails.validateSpells(spellsBySpellLevelByClass);
          secondaryClassDetails.validateSelectedOptions();
        }
        const spellSlots = SpellService.getSpellSlots(primaryClassDetails, secondaryClassDetails);
        const warlockSpellSlots = SpellService.getWarlockSpellSlots(primaryClassDetails.classInfo, primaryClassDetails.classLevel) ||
                                  SpellService.getWarlockSpellSlots(secondaryClassDetails.classInfo || null, secondaryClassDetails.classLevel || 0);

        SpellService.addSpells(spellSections, primaryClassDetails, secondaryClassDetails, spellSlots, warlockSpellSlots);

        // This Challenge Rating is a bit of a guess. a 20th level PC should be around CR 12, so I assume a 3/5 conversion.
        // But it wont work accurately across the board.
        const cr = Math.round(combinedLevel * 3 / 5);
        const savingProfs = [];
        for (const attribute of primaryClassDetails.classInfo.prof_saving_throws.split(",")) {
            const shortName = attribute.trim().substring(0, 3).toUpperCase();
            const attr = attribute.toLowerCase() as keyof AttributeValues;
            const saveLevel = Attributes.getBonus(attributeValues[attr]) + proficiencyBonus;
            savingProfs.push(`${shortName} +${saveLevel}`);
        }
        const percSkill = skillSet.skills['Perception'];
        const passivePerception = 10 + (percSkill?.adjLevel ?? Attributes.getBonus(attributeValues.wisdom));

        const mon: Monster = new Monster();
        mon.file_type          = 'dnd monster';
        mon.inventory          = [];
        mon.hitDiceNumber      = combinedLevel;
        mon.hitDiceType        = hitDiceType;
        mon.hitDiceBonus       = hitDiceBonus;
        mon.hitPointsCustom    = hitPointsCustom;
        mon.usePcHitDice       = true;
        mon.shortName          = "";
        mon.pluralName         = "";
        mon.name               = npcName;
        mon.size               = size;
        mon.type               = "Humanoid";
        mon.subtype            = selectedRace?.name ?? '';
        mon.hit_points         = hitPoints;
        mon.hit_dice           = hitDice;
        mon.speed              = speed;
        mon.attributes         = attributeValues;
        mon.skillSet           = skillSet;
        mon.senses             = senses;
        mon.languages          = langs.join(', ');
        mon.challenge_rating   = "" + cr;
        mon.cr                 = cr;
        mon.proficiencyBonus   = proficiencyBonus;
        mon.xp                 = Utils.getXpByCR(combinedLevel);
        mon.passivePerception  = passivePerception;
        mon.traits             = sections;
        mon.spells             = spellSections;
        mon.damage_resistances = damageResistancesArray.join(", ");
        mon.bonus_actions      = bonus_actions;
        mon.reactions          = reactions;
        mon.saving_throws      = savingProfs.join(', ');
        mon.armor              = equipedArmor;
        mon.armor_class        = equipedArmorClass;
        mon.armor_desc         = equipedArmorDesc;

        let gear: string[] = [...equipmentRaceSelected, ...equipmentClassSelected, ...equipmentBackgroundSelected];
        if (equipedArmor) {
          // If we selected an armor type, dont list any armor we got from our class/race/background
          gear = gear.filter(g => !Armor.getArmorNames().map(a => a.toLowerCase()).includes(g.toLowerCase()));
          gear = gear.filter(g => g.toLowerCase() !== 'shield')
                     .map(a => a.toLowerCase().startsWith('a shield and') ? a.substring('a shield and'.length) : a.replace(' and a shield', ''));
          if (equipedArmorDesc?.includes('Shield')) {
            gear.unshift('Shield');
          }
          gear.unshift(equipedArmor.name);
        }
        for (const equipedWeapon of [equipedWeapon3, equipedWeapon2, equipedWeapon1]) {
          if (equipedWeapon) {
            let abilites = Abilities.weapons.filter(w => (w.name?.toLowerCase() === equipedWeapon.toLowerCase()));
            if (abilites.length === 2) {
              if (abilites[0].weapon_data?.attribute === 'finese') {
                const attr = (attributeValues.dexterity > attributeValues.strength) ? 'dexterity' : 'strength';
                abilites = abilites.filter(w => w.attribute?.toLowerCase() === attr);
              }
            }
            if (abilites.length === 1) {
              mon.actions.unshift(abilites[0]);
              gear.unshift(abilites[0].name);
            }
          }
        }
        mon.gear = gear.join(', ');

        // store the NPC data, so this data can be editted
        const raceOptions = mapToString(raceOptionsSelectedArrayByOptionNameMap);
        const primaryClassOptions = mapToString(primaryClassDetails.optionsSelectedArrayByOptionNameMap);
        const secondaryClassOptions = mapToString(secondaryClassDetails.optionsSelectedArrayByOptionNameMap || new Map());
        const npcData: NpcData = {
                        name: npcName,
                        raceName: selectedRace?.name ?? '',
                        raceOptions, attributeBases, diceArray, attributeValues, asiRacialSelected: [...asiRacialSelected],
                        asiClassSelected: [...asiClassSelected],
                        backgroundName: selectedBackground?.name ?? '',
                        primaryClassName: primaryClassDetails.classInfo.name,
                        primaryArchetypeName: primaryClassDetails.archetype?.name || '',
                        primaryLevel: primaryClassDetails.classLevel,
                        primaryClassOptions,
                        secondaryClassName: secondaryClassDetails.classInfo?.name || '',
                        secondaryArchetypeName: secondaryClassDetails.archetype?.name || '',
                        secondaryLevel: secondaryClassDetails.classLevel || 0,
                        secondaryClassOptions,
                        langsRaceSelected: [...langsRaceSelected],
                        langsClassSelected: [...langsClassSelected],
                        langsBackgroundSelected: [...langsBackgroundSelected],
                        skillsRaceSelected: [...skillsRaceSelected],
                        skillsClassSelected: [...skillsClassSelected],
                        skillsBackgroundSelected: [...skillsBackgroundSelected],
                        spellbookSpellsArrayBySpellLevelPrimary: [...primaryClassDetails.spellbookSpellsArrayBySpellLevel],
                        spellbookSpellsArrayBySpellLevelSecondary: [...(secondaryClassDetails.spellbookSpellsArrayBySpellLevel || [])],
                        spellsPreparedArrayBySpellLevelPrimary: [...primaryClassDetails.spellsPreparedArrayBySpellLevel],
                        spellsPreparedArrayBySpellLevelSecondary: [...(secondaryClassDetails.spellsPreparedArrayBySpellLevel || [])],
                        spellbookSpellKeysBySpellLevelPrimary:   NpcData.getKeysFromSpells(primaryClassDetails.spellbookSpellsArrayBySpellLevel),
                        spellbookSpellKeysBySpellLevelSecondary: NpcData.getKeysFromSpells(secondaryClassDetails.spellbookSpellsArrayBySpellLevel),
                        spellKeysPreparedBySpellLevelPrimary:    NpcData.getKeysFromSpells(primaryClassDetails.spellsPreparedArrayBySpellLevel),
                        spellKeysPreparedBySpellLevelSecondary:  NpcData.getKeysFromSpells(secondaryClassDetails.spellsPreparedArrayBySpellLevel),
                        primaryAttribute:   '',
                        secondaryAttribute: '',
                      };
        mon.npcData = npcData;
        Open5e.computeSplitPoint(mon);
        onClose(mon);
    }

    const copyMap = (origOptionsSelectedArrayByNameMap: Map<string, string[]>, newName: string, newSelectedArray: string[]):Map<string, string[]> => {
        const deepCopy = new Map();

        for (const [name, selectedArray] of origOptionsSelectedArrayByNameMap) {
          deepCopy.set(name, [...selectedArray]);
        }
        deepCopy.set(newName, newSelectedArray);
        return deepCopy;
    }

    const handleClassLevelChange = (level: number, isPrimary: boolean) => {
      if (isPrimary) {
        primaryClassDetails.classLevel = level;
        setPrimaryClassDetails(primaryClassDetails.clone());
      } else {
        secondaryClassDetails.classLevel = level;
        setSecondaryClassDetails(secondaryClassDetails.clone());
      }
      setAbilitiesDone(false);
      setLanguagesDone(false);
      setSkillsDone(false);
      setSpellbookDone(false)
      setCantripsDone(false);
      setSpellsDone(false);
    }
    const handleClassOptionChange = (optionName: string, optionValues: string[], isPrimary: boolean) => {
      if (isPrimary) {
        primaryClassDetails.optionsSelectedArrayByOptionNameMap = 
              copyMap(primaryClassDetails.optionsSelectedArrayByOptionNameMap, optionName, optionValues);
        setPrimaryClassDetails(primaryClassDetails.clone());
      } else {
        secondaryClassDetails.optionsSelectedArrayByOptionNameMap =
              copyMap(secondaryClassDetails.optionsSelectedArrayByOptionNameMap, optionName, optionValues);
        setSecondaryClassDetails(secondaryClassDetails.clone());
      }
    }
    const handleClassChange = (cls: Class | null, archetype: Class | null, isPrimary: boolean) => {
      if (isPrimary) {
        primaryClassDetails.classInfo = cls; 
        primaryClassDetails.archetype = archetype;
        setPrimaryClassDetails(primaryClassDetails.clone());
      } else {
        secondaryClassDetails.classInfo = cls; 
        secondaryClassDetails.archetype = archetype;
        setSecondaryClassDetails(secondaryClassDetails.clone());
      }
      setAbilitiesDone(false);
      setLanguagesDone(false);
      setSkillsDone(false);
      setSpellbookDone(false)
      setCantripsDone(false)
      setSpellsDone(false)
    }

    const handleSpellbookChanged = (level: number, spellsSelected: Spell[], isPrimary: boolean) => {
      const classDetails = (isPrimary? primaryClassDetails : secondaryClassDetails);
      const newSpellbookSpellsArrayBySpellLevel = [...classDetails.spellbookSpellsArrayBySpellLevel];
      newSpellbookSpellsArrayBySpellLevel[level] = spellsSelected;
      classDetails.spellbookSpellsArrayBySpellLevel = newSpellbookSpellsArrayBySpellLevel;
      if (isPrimary) {
        setPrimaryClassDetails(primaryClassDetails.clone());
      } else {
        setSecondaryClassDetails(secondaryClassDetails.clone());
      }
    }
  
    const handleSpellsSelectedChanged = (level: number, spellsSelected: Spell[], isPrimary: boolean) => {
      const classDetails = (isPrimary? primaryClassDetails : secondaryClassDetails);
      const newSpellsPreparedArrayBySpellLevel = [...classDetails.spellsPreparedArrayBySpellLevel];
      newSpellsPreparedArrayBySpellLevel[level] = spellsSelected;
      classDetails.spellsPreparedArrayBySpellLevel = newSpellsPreparedArrayBySpellLevel;
      if (isPrimary) {
        setPrimaryClassDetails(primaryClassDetails.clone());
      } else {
        setSecondaryClassDetails(secondaryClassDetails.clone());
      }
    }
      
    const getLoadingBlock = (section: string) => (<>
      Please wait for the <b>'{section}'</b> data to load.<br/>
      Cache loading status:<br/>
      <CacheBlock show={true} showClearButton={false} dataModel={dataModel}></CacheBlock>
    </>);

    return (
        <div className="modal-overlay" onClick={() => onClose(null)}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <span className="modal-title">Creating PC/NPC</span>
                    <span className="name-label" style={{color: 'gray', paddingRight: '10%'}}>
                      Name: <input type='text' value={npcName} onChange={(event) => setNpcName(event.target.value)}
                       style ={{fontSize: 'x-large', background: '#FFFFFFaa', border: 'solid 1px #00000040' }}/>
                    </span>
                </div>
                <div className="modal-header">
                    <button onClick={() => handleTabClick('race')}       className={activeTab === 'race'       ? 'active' : ''}>Race</button>
                    <button onClick={() => handleTabClick('class')}      className={activeTab === 'class'      ? 'active' : ''}>Class</button>
                    <button onClick={() => handleTabClick('class2')}     className={activeTab === 'class2'     ? 'active' : ''} disabled={!primaryClassDetails.classInfo}>2<sup style={{fontSize: 'small'}}>nd</sup>&nbsp;Class</button>
                    <button onClick={() => handleTabClick('attributes')} className={activeTab === 'attributes' ? 'active' : ''}>Attributes</button>
                    <button onClick={() => handleTabClick('background')} className={activeTab === 'background' ? 'active' : ''}>Background</button>
                    <button onClick={() => handleTabClick('skills')}     className={activeTab === 'skills'     ? 'active' : ''}>Skills</button>{/*langauges, proficiencies, skills*/}
                    <button onClick={() => handleTabClick('spells')}     className={activeTab === 'spells'     ? 'active' : ''}>Spells</button>
                    <button onClick={() => handleTabClick('equipment')}  className={activeTab === 'equipment'  ? 'active' : ''}>Equipment</button>
                    <div className='tab-endspace'></div>
                </div> 
                <div className="modal-body">
                    {activeTab === 'race' && races && (races.length > 0) && (<>
                        <RacePage races={races} selectedRace={selectedRace} onRaceChange={handleRaceChange} documentSources={documentSources}
                                  optionsSelectedArrayByOptionNameMap={raceOptionsSelectedArrayByOptionNameMap} 
                                  onSetOption={(optName, selectedOptionsArray) => setRaceOptionsSelectedArrayByOptionNameMap(copyMap(raceOptionsSelectedArrayByOptionNameMap, optName, selectedOptionsArray))} />
                                  </>)
                                  }
                    {activeTab === 'race' && (races == null || races.length == 0) && getLoadingBlock('Race')}
                    {activeTab === 'class' && classes && (classes.length > 0) && primaryClassDetails && 
                        <ClassPage classes={classes} isPrimary={true}
                                  selectedClass={primaryClassDetails}
                                  selectedArchetype={primaryClassDetails.archetype || null}
                                  onClassChange={(cls, archetype) => handleClassChange(cls, archetype, true)}
                                  onLevelChange={(level) => handleClassLevelChange(level, true)}
                                  maxLevel={20 - ((secondaryClassDetails.classInfo ? secondaryClassDetails.classLevel : 0) || 0)}
                                  optionsSelectedArrayByOptionNameMap={primaryClassDetails.optionsSelectedArrayByOptionNameMap}
                                  onSetOption={(optionName, optionValues) => handleClassOptionChange(optionName, optionValues, true)} />
                                  }
                    {(activeTab === 'class' || activeTab === 'class2') && (classes == null || classes.length == 0) && getLoadingBlock('Class')}
                    {activeTab === 'class2' && classes && (classes.length > 0) && (multiClassError || (secondaryClassDetails && (
                        <ClassPage classes={classes} isPrimary={false}
                                  selectedClass={secondaryClassDetails}
                                  selectedArchetype={secondaryClassDetails.archetype || null}
                                  onClassChange={(cls, archetype) => handleClassChange(cls, archetype, false)}
                                  onLevelChange={(level) => handleClassLevelChange(level, false)}
                                  allowedClassNames={allowedSecondaryClassNames}
                                  maxLevel={20 - (primaryClassDetails.classLevel || 0)}
                                  optionsSelectedArrayByOptionNameMap={secondaryClassDetails.optionsSelectedArrayByOptionNameMap}
                                  onSetOption={(optionName, optionValues) => handleClassOptionChange(optionName, optionValues, false)} />
                    )))}
                    {activeTab === 'attributes' &&
                        <AbilitiesPage race={selectedRace}  diceArray={diceArray} setDiceArray={setDiceArray}
                                      attributeBases={attributeBases} onAttributeBaseChanged={(attrs) => setAttributeBases(attrs)}
                                      attributeValues={attributeValues} onAttributeValuesChanged={handleAttributeValuesChanged}
                                      asiRacialOptions={asiRacialOptions}
                                      asiRacialSelected={asiRacialSelected} onAsiRacialSelectedChanged={(newVal) => setAsiRacialSelected(newVal)}
                                      asiClassOptions={asiClassOptions}
                                      asiClassSelected={asiClassSelected} onAsiClassSelectedChanged={(newVal) => setAsiClassSelected(newVal)}
                                      featsByName={featsByName} />}
                    {activeTab === 'background' && (!backgrounds || (backgrounds.length == 0)) && getLoadingBlock('Background')}
                    {activeTab === 'background' && backgrounds && (backgrounds.length > 0) && 
                        <BackgroundPage selectedBackground={selectedBackground} onBackgroundChange={handleBackgroundChanges}
                                        documentSources={documentSources} backgrounds={backgrounds} />}
                    {activeTab === 'skills' && (
                    <table>
                      <tbody>
                        <tr style={{height: '20px'}}>
                          <td rowSpan={2} style={{borderRight: '1px solid', verticalAlign: 'top'}}>
                            <ItemSection racialSelected={langsRaceSelected}          racialOptions={langsRaceOptions}           onRacialSelectedChanged={(s) => setLangsRaceSelected(s)}
                                        classSelected={langsClassSelected}           classOptions={langsClassOptions}           onClassSelectedChanged={(s) => setLangsClassSelected(s)}
                                        backgroundSelected={langsBackgroundSelected} backgroundOptions={langsBackgroundOptions} onBackgroundSelectedChanged={(s) => setLangsBackgroundSelected(s)}
                                        possibleSet={Utils.langs} name='Languages' />
                          </td>
                          <td rowSpan={2} style={{borderRight: '1px solid', verticalAlign: 'top'}}>
                            <ItemSection racialSelected={skillsRaceSelected}          racialOptions={skillsRaceOptions}           onRacialSelectedChanged={(s) => setSkillsRaceSelected(s)}
                                        classSelected={skillsClassSelected}           classOptions={skillsClassOptions}           onClassSelectedChanged={(s) => setSkillsClassSelected(s)}
                                        backgroundSelected={skillsBackgroundSelected} backgroundOptions={skillsBackgroundOptions} onBackgroundSelectedChanged={(s) => setSkillsBackgroundSelected(s)}
                                        possibleSet={possibleSkills} name='Skills' />
                          </td>
                          <td colSpan={2} style={{verticalAlign: 'top'}}>
                            <div style={{textAlign: 'center', fontWeight: '700', fontSize: 'Larger', color: 'grey', textDecoration: 'underline'}}>Procifiencies</div>
                          </td>
                          </tr>
                          <tr>
                            <td style={{borderRight: '1px solid', verticalAlign: 'top'}}>
                              <ItemSection racialSelected={armorRaceSelected}          racialOptions={armorRaceOptions}           onRacialSelectedChanged={(s) => setArmorRaceSelected(s)}
                                          classSelected={armorClassSelected}           classOptions={armorClassOptions}           onClassSelectedChanged={(s) => setArmorClassSelected(s)}
                                          backgroundSelected={armorBackgroundSelected} backgroundOptions={armorBackgroundOptions} onBackgroundSelectedChanged={(s) => setArmorBackgroundSelected(s)}
                                          possibleSet={null} name='Armor' />
                            </td>
                            <td style={{verticalAlign: 'top'}}>
                              <ItemSection racialSelected={weaponsRaceSelected}          racialOptions={weaponsRaceOptions}           onRacialSelectedChanged={(s) => setWeaponsRaceSelected(s)}
                                          classSelected={weaponsClassSelected}           classOptions={weaponsClassOptions}           onClassSelectedChanged={(s) => setWeaponsClassSelected(s)}
                                          backgroundSelected={weaponsBackgroundSelected} backgroundOptions={weaponsBackgroundOptions} onBackgroundSelectedChanged={(s) => setWeaponsBackgroundSelected(s)}
                                          possibleSet={null} name='Weapons' />
                            </td>
                        </tr>
                      </tbody>
                    </table>
                    )}
                    {activeTab === 'spells' && (!spellsBySpellLevelByClass || (spellsBySpellLevelByClass.size === 0) || (dataModel.spells.totalCount !== dataModel.spells.data.length)) && getLoadingBlock('Spell')}
                    {activeTab === 'spells' && spellsBySpellLevelByClass && (spellsBySpellLevelByClass.size > 0) && (
                      <SpellLevelsSelector primaryClassDetails={primaryClassDetails}
                                          secondaryClassDetails={secondaryClassDetails}
                                          attributeValues={attributeValues} spellsBySpellLevelByClass={spellsBySpellLevelByClass}
                                          onSpellbookSpellsPrimaryChanged={  (level, spellNames) => handleSpellbookChanged(level, spellNames, true)}
                                          onSpellsPreparedPrimaryChanged={   (level, spellNames) => handleSpellsSelectedChanged(level, spellNames, true)}
                                          onSpellbookSpellsSecondaryChanged={(level, spellNames) => handleSpellbookChanged(level, spellNames, false)}
                                          onSpellsPreparedSecondaryChanged={ (level, spellNames) => handleSpellsSelectedChanged(level, spellNames, false)}
                                          openLevel={openLevel}
                                          setOpenLevel={setOpenLevel}
                                          setClassDetails={undefined}
                                          onClickOutside={() => setOpenLevel(-1)}
                            />
                      )}
                    {(activeTab === 'equipment') && (classes == null || classes.length == 0) && getLoadingBlock('Class')}
                    {activeTab === 'equipment' && classes && (classes.length > 0) && (
                      <table>
                        <tbody>
                          <tr>
                            <td style={{borderRight: '1px solid', verticalAlign: 'top', padding: '10px'}}>
                              <ItemSection racialSelected={equipmentRaceSelected}          racialOptions={equipmentRaceOptions}           onRacialSelectedChanged={(s) => setEquipmentRaceSelected(s)}
                                          classSelected={equipmentClassSelected}           classOptions={equipmentClassOptions}           onClassSelectedChanged={(s) => setEquipmentClassSelected(s)}
                                          backgroundSelected={equipmentBackgroundSelected} backgroundOptions={equipmentBackgroundOptions} onBackgroundSelectedChanged={(s) => setEquipmentBackgroundSelected(s)}
                                          possibleSet={null} name='Starting Equipment' />
                            </td>
                            <td style={{verticalAlign: 'top', padding: '10px'}}>
                              <div style={{fontWeight: '600', textAlign: 'center', padding: '10px'}}>Current Equipment:</div>
                              <div style={{fontWeight: '500', textAlign: 'left'}}>Armor:</div>
                              <table>
                                <tbody>
                                  <tr>
                                    <td style={{fontWeight: '600', fontStyle: 'italic'}}>Profs:</td>
                                    <td style={{fontWeight: '300', fontStyle: 'italic'}}>{armorProficiencies.join(", ")}</td>
                                  </tr>
                                </tbody>
                              </table>
                              Armor:
                              &nbsp;
                              <span style={{border: 'solid 1px grey', borderRadius: '5px', background: 'white'}}>
                                <ArmorSelector armorType={equipedArmor} armorClass={equipedArmorClass} armorDesc={equipedArmorDesc} useMm2024={false}
                                               dexterity={attributeValues.dexterity} strength={attributeValues.strength} isOpen={armorBlockOpen} 
                                               setIsOpen={(newIsOpen) => {setArmorBlockOpen(newIsOpen)}}
                                               onSave={handleUpdateArmor} />
                              </span>
                              <br/>
                              <br/>
                              <hr/>
                              <br/>
                              <div style={{fontWeight: '500', textAlign: 'left'}}>Weapons:</div>
                              <table>
                                <tbody>
                                  <tr>
                                    <td style={{fontWeight: '600', fontStyle: 'italic'}}>Profs:</td>
                                    <td style={{fontWeight: '300', fontStyle: 'italic'}}>{weaponProficiencies.join(", ")}</td>
                                  </tr>
                                </tbody>
                              </table>
                              <table>
                                <tbody>
                                  <tr>
                                    <td></td>
                                    <td style={{fontSize:'smaller', fontStyle: 'italic'}}>
                                      <input type="checkbox" id="onlyProfWeapons" name="onlyProfWeapons" checked={onlyProfWeapons} onChange={() => setOnlyProfWeapons((prev) => !prev)}/>
                                      <label htmlFor="onlyProfWeapons">Only show weapons in the proficiency set above</label>
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Primary:</td>
                                    <td>
                                      <select value={equipedWeapon1}
                                          style={{ width: 'auto', minWidth: '100px', margin: '2px' }}
                                          onChange={(e) => setEquipedWeapon1(e.target.value)}>
                                          <option value=''>-- Select --</option>
                                          {
                                              (onlyProfWeapons ? profWeaponNames : possibleWeaponNames).map(option => (
                                                  <option key={option} value={option}>
                                                      {option}
                                                  </option>
                                              ))
                                          }
                                      </select>
                                      {equipedWeapon1Conflict && (<>
                                          <br/>
                                          <div style={{color: 'red', fontStyle: 'italic', fontSize: 'x-small'}}>This is two-handed weapon, and you have a shield!</div>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Secondary:</td>
                                    <td>
                                      <select value={equipedWeapon2}
                                          style={{ width: 'auto', minWidth: '100px', margin: '2px' }}
                                          onChange={(e) => setEquipedWeapon2(e.target.value)}>
                                          <option value=''>None</option>
                                          {
                                              (onlyProfWeapons ? profWeaponNames : possibleWeaponNames).map(option => (
                                                  <option key={option} value={option}>
                                                      {option}
                                                  </option>
                                              ))
                                          }
                                      </select>
                                      {equipedWeapon2Conflict && (<>
                                          <br/>
                                          <div style={{color: 'red', fontStyle: 'italic', fontSize: 'x-small'}}>This is two-handed weapon, and you have a shield!</div>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td>Alternate:</td>
                                    <td>
                                      <select value={equipedWeapon3}
                                          style={{ width: 'auto', minWidth: '100px', margin: '2px' }}
                                          onChange={(e) => setEquipedWeapon3(e.target.value)}>
                                          <option value=''>None</option>
                                          {
                                              (onlyProfWeapons ? profWeaponNames : possibleWeaponNames).map(option => (
                                                  <option key={option} value={option}>
                                                      {option}
                                                  </option>
                                              ))
                                          }
                                      </select>
                                      {equipedWeapon3Conflict && (<>
                                          <br/>
                                          <div style={{color: 'red', fontStyle: 'italic', fontSize: 'x-small'}}>This is two-handed weapon, and you have a shield!</div>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                </div>
                <button onClick={() => onClose(null)}>Cancel</button>
                <button disabled={!primaryClassDetails?.classInfo} onClick={() => onAccept()}>Accept</button>
                {getIncompleteMessage()}
            </div>
        </div>
    );
};
export default CharacterModal;
